import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import {
  getCronResolverConfigs,
  addCronResolverConfig,
  updateCronResolverConfig,
  deleteCronResolverConfig,
  getCronLogStats,
} from "../../redux/features/clicksSlice";
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, Chip, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  Autocomplete, InputAdornment, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BarChartIcon from "@mui/icons-material/BarChart";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";

const EMPTY_FORM = {
  store_id: "",
  type: 2,
  final_domain: "",
  cron_interval: 3,
  country: "US",
  description: "",
  active: 1,
};

const COUNTRIES = ["US", "IN", "GB", "DE", "FR", "AU", "CA", "SG", "AE", "BR", "JP", "NL"];

const ConfigDialog = ({ open, onClose, initial, siteList, onSave }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);

  useEffect(() => {
    setForm(initial || EMPTY_FORM);
  }, [initial, open]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const isEdit = !!initial?.id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {isEdit ? `Edit Config #${initial.id}` : "Add New Cron Config"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              size="small"
              options={siteList}
              getOptionLabel={(option) => `${option.id} — ${option.site_name}`}
              value={siteList.find(s => s.id == form.store_id) || null}
              onChange={(event, newValue) => {
                set("store_id", newValue ? newValue.id : "");
              }}
              renderInput={(params) => (
                <TextField {...params} label="Store (Campaign)" variant="outlined" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Link Type (e.g. 2)"
              type="number"
              size="small"
              fullWidth
              value={form.type}
              onChange={e => set("type", Number(e.target.value))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Final Domain (e.g. dhgate.com)"
              size="small"
              fullWidth
              value={form.final_domain}
              onChange={e => set("final_domain", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Interval (minutes)"
              type="number"
              size="small"
              fullWidth
              value={form.cron_interval}
              onChange={e => set("cron_interval", Number(e.target.value))}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Proxy Country</InputLabel>
              <Select
                value={form.country}
                label="Proxy Country"
                onChange={e => set("country", e.target.value)}
              >
                {COUNTRIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.active == 1}
                  onChange={e => set("active", e.target.checked ? 1 : 0)}
                  color="success"
                />
              }
              label="Active"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description (optional)"
              size="small"
              fullWidth
              value={form.description}
              onChange={e => set("description", e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">Cancel</Button>
        <Button
          onClick={() => onSave(form)}
          variant="contained"
          disabled={!form.store_id || !form.final_domain}
        >
          {isEdit ? "Update & Reload Cron" : "Add & Start Cron"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CronResolverConfig = () => {
  const dispatch = useDispatch();
  const [configs, setConfigs] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("last_run_at_desc");

  const { cronLogStats } = useSelector((state) => state.clicks);
  const [graphModalOpen, setGraphModalOpen] = useState(false);
  const [selectedGraphStoreId, setSelectedGraphStoreId] = useState(null);
  const [graphDays, setGraphDays] = useState(7);

  useEffect(() => {
    if (graphModalOpen && selectedGraphStoreId) {
      dispatch(getCronLogStats({ store_id: selectedGraphStoreId, days: graphDays }));
    }
  }, [graphModalOpen, selectedGraphStoreId, graphDays, dispatch]);

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, siteRes] = await Promise.all([
        dispatch(getCronResolverConfigs()),
        dispatch({ type: "clicks/getsiteid" }), // just use direct API
      ]);
      if (cfgRes.payload) setConfigs(cfgRes.payload);

      // Load sites separately
      const { getCronResolverConfigsApi } = await import("../../redux/api/clicksApi");
      const { getSiteIdApi } = await import("../../redux/api/clicksApi");
      const sitesResp = await getSiteIdApi();
      if (sitesResp.data?.data) setSiteList(sitesResp.data.data);
    } catch (e) {
      toast.error("Failed to load configs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (form) => {
    try {
      if (form.id) {
        await dispatch(updateCronResolverConfig(form)).unwrap();
        toast.success("Config updated — cron reloaded!");
      } else {
        await dispatch(addCronResolverConfig(form)).unwrap();
        toast.success("Config added — cron started!");
      }
      setDialogOpen(false);
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error("Failed to save config");
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteCronResolverConfig({ id })).unwrap();
      toast.success("Config deleted — cron reloaded");
      setDeleteConfirmId(null);
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleToggleActive = async (conf) => {
    try {
      await dispatch(updateCronResolverConfig({ id: conf.id, active: conf.active == 1 ? 0 : 1 })).unwrap();
      toast.success(`Config ${conf.active == 1 ? "paused" : "resumed"} — cron reloaded`);
      load();
    } catch (e) {
      toast.error("Failed to toggle");
    }
  };

  const getSiteName = (storeId) => siteList.find(s => s.id == storeId)?.site_name || "";

  // Filter and Sort Logic
  const filteredConfigs = configs
    .filter(conf => {
      const sTerm = searchTerm.toLowerCase();
      const siteName = getSiteName(conf.store_id).toLowerCase();
      return (
        conf.store_id.toString().includes(sTerm) ||
        conf.final_domain.toLowerCase().includes(sTerm) ||
        conf.description?.toLowerCase().includes(sTerm) ||
        siteName.includes(sTerm)
      );
    })
    .sort((a, b) => {
      if (sortBy === "last_run_at_desc") {
        return new Date(b.last_run_at || 0) - new Date(a.last_run_at || 0);
      }
      if (sortBy === "last_run_at_asc") {
        return new Date(a.last_run_at || 0) - new Date(b.last_run_at || 0);
      }
      if (sortBy === "id_desc") return b.id - a.id;
      return 0;
    });

  return (
    <Box sx={{ padding: { xs: "8px", sm: "16px" }, minHeight: "100vh", backgroundColor: "#f4f7f9" }}>
      <Helmet><title>Alpha Targeting || Cron URL Resolver</title></Helmet>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="primary.main">
            Cron URL Resolver
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Manages automatic link2 resolution for campaign URLs from the DB.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setEditTarget(null); setDialogOpen(true); }}
          >
            Add Config
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search Store, Domain, or ID"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={e => setSortBy(e.target.value)}
              >
                <MenuItem value="last_run_at_desc">Last Success (Newest)</MenuItem>
                <MenuItem value="last_run_at_asc">Last Success (Oldest)</MenuItem>
                <MenuItem value="id_desc">Recently Added</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={5} sx={{ textAlign: "right" }}>
            <Typography variant="caption" color="textSecondary">
              Showing {filteredConfigs.length} of {configs.length} configs
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : filteredConfigs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: "12px" }}>
          <Typography color="textSecondary">No cron configs found matching your criteria.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredConfigs.map(conf => (
            <Grid item xs={12} sm={6} md={4} key={conf.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  borderColor: conf.active == 1 ? "#4caf50" : "#e0e0e0",
                  boxShadow: conf.active == 1 ? "0 2px 12px rgba(76,175,80,0.12)" : "none",
                  opacity: conf.active == 1 ? 1 : 0.7,
                }}
              >
                <CardContent sx={{ pb: "12px !important" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                        #{conf.id} — {conf.final_domain}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Store {conf.store_id} {getSiteName(conf.store_id) ? `(${getSiteName(conf.store_id)})` : ""}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={conf.active == 1 ? "Active" : "Paused"}
                      color={conf.active == 1 ? "success" : "default"}
                      onClick={() => handleToggleActive(conf)}
                      clickable
                    />
                  </Box>

                  {conf.description && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      {conf.description}
                    </Typography>
                  )}

                  <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={6}>
                      <Paper variant="outlined" sx={{ p: 1, textAlign: "center", borderRadius: "8px" }}>
                        <Typography variant="caption" color="textSecondary" display="block">Type</Typography>
                        <Typography variant="body2" fontWeight="bold">{conf.type}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper variant="outlined" sx={{ p: 1, textAlign: "center", borderRadius: "8px" }}>
                        <Typography variant="caption" color="textSecondary" display="block">Country</Typography>
                        <Typography variant="body2" fontWeight="bold">{conf.country}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 1, textAlign: "center", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                        <Typography variant="caption" color="textSecondary">Every</Typography>
                        <Typography variant="body2" fontWeight="bold">{conf.cron_interval} min</Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {conf.last_attempt_at && (
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                      Last attempt: {new Date(conf.last_attempt_at).toLocaleString()}
                      {conf.last_status && (
                        <Chip 
                          size="small" 
                          label={conf.last_status} 
                          color={conf.last_status === "success" ? "success" : "error"} 
                          variant="outlined"
                          sx={{ height: 16, fontSize: '10px', ml: 1, verticalAlign: 'middle' }}
                        />
                      )}
                    </Typography>
                  )}

                  {conf.last_run_at && (
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1, fontWeight: 'bold', color: 'success.main' }}>
                      Last success: {new Date(conf.last_run_at).toLocaleString()}
                    </Typography>
                  )}

                  {conf.links && conf.links.length > 0 && (() => {
                    const successCount = conf.links.filter(l => l.last_status === 'success').length;
                    return (
                    <Accordion sx={{ mb: 2, boxShadow: 'none', border: '1px solid #eee', borderRadius: '8px', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2" fontWeight="bold">
                          View Links ({successCount}/{conf.links.length} working)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <TableContainer sx={{ maxHeight: 300 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Link</TableCell>
                                <TableCell>Status (Hover for reason)</TableCell>
                                <TableCell>Last Attempt</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {conf.links.map(link => (
                                <TableRow key={link.link_id}>
                                  <TableCell sx={{ fontSize: '12px' }}>{link.link_id}</TableCell>
                                  <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '12px' }}>
                                    <Tooltip title={link.link}>
                                      <span>{link.link}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={link.last_reason || "No reason available"}>
                                      <Chip 
                                        size="small" 
                                        label={link.last_status || "Pending"} 
                                        color={link.last_status === "success" ? "success" : link.last_status === "failure" ? "error" : "default"} 
                                        sx={{ fontSize: '10px', height: 18 }}
                                      />
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '11px' }}>
                                    {link.last_attempt_at ? new Date(link.last_attempt_at).toLocaleString() : "Never"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                    );
                  })()}

                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      component={Link}
                      to={`/camplinks?store_id=${conf.store_id}`}
                      fullWidth
                    >
                      View Links
                    </Button>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<BarChartIcon />}
                      onClick={() => {
                        setSelectedGraphStoreId(conf.store_id);
                        setGraphDays(7);
                        setGraphModalOpen(true);
                      }}
                      fullWidth
                    >
                      Plot Graph
                    </Button>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => { setEditTarget(conf); setDialogOpen(true); }}
                      fullWidth
                    >
                      Edit
                    </Button>
                    <Tooltip title="Delete config">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(conf.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Dialog */}
      <ConfigDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditTarget(null); }}
        initial={editTarget}
        siteList={siteList}
        onSave={handleSave}
      />

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Config #{deleteConfirmId}?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently remove the config and stop its cron schedule immediately.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirmId)}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Graph Modal */}
      <Dialog open={graphModalOpen} onClose={() => setGraphModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Store {selectedGraphStoreId} Link Statistics</Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Days</InputLabel>
            <Select value={graphDays} label="Days" onChange={(e) => setGraphDays(e.target.value)}>
              <MenuItem value={7}>7 Days</MenuItem>
              <MenuItem value={15}>15 Days</MenuItem>
              <MenuItem value={30}>30 Days</MenuItem>
              <MenuItem value={45}>45 Days</MenuItem>
            </Select>
          </FormControl>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Hourly Stats Chart */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: '600', color: '#64748b' }}>
                  Hourly Stats (Last 24h)
                </Typography>
                <Chart
                  options={{
                    chart: { type: 'bar', stacked: true, toolbar: { show: false } },
                    plotOptions: { bar: { columnWidth: '60%', borderRadius: 2 } },
                    colors: ['#FF6347', '#00BA71'],
                    xaxis: { categories: cronLogStats?.data?.hourly?.map(item => item.hour) || [], labels: { style: { fontSize: '10px' } } },
                    yaxis: { labels: { formatter: (val) => Math.floor(val) } },
                    legend: { position: 'bottom' },
                    dataLabels: { enabled: false },
                    tooltip: { shared: true, intersect: false }
                  }}
                  series={[
                    { name: 'Failed', data: cronLogStats?.data?.hourly?.map(item => Number(item.failed)) || [] },
                    { name: 'Successful', data: cronLogStats?.data?.hourly?.map(item => Number(item.successful)) || [] },
                  ]}
                  type="bar" height={220}
                />
              </Paper>
            </Grid>

            {/* Daily Stats Chart */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: '600', color: '#64748b' }}>
                  Daily Stats (Last {graphDays} Days)
                </Typography>
                <Chart
                  options={{
                    chart: { type: 'bar', stacked: true, toolbar: { show: false } },
                    plotOptions: { bar: { columnWidth: '50%', borderRadius: 2 } },
                    colors: ['#FF6347', '#00BA71'],
                    xaxis: { categories: cronLogStats?.data?.daily?.map(item => item.date) || [], labels: { style: { fontSize: '10px' } } },
                    yaxis: { labels: { formatter: (val) => Math.floor(val) } },
                    legend: { position: 'bottom' },
                    dataLabels: { enabled: false },
                    tooltip: { shared: true, intersect: false }
                  }}
                  series={[
                    { name: 'Failed', data: cronLogStats?.data?.daily?.map(item => Number(item.failed)) || [] },
                    { name: 'Successful', data: cronLogStats?.data?.daily?.map(item => Number(item.successful)) || [] },
                  ]}
                  type="bar" height={220}
                />
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CronResolverConfig;
